import {isEqual} from 'lodash';
import memoizeOne from 'memoize-one';
import React, {FC} from 'react';

import {DataEventTrackingProvider} from '../../../../libs/shared/core/src/services/tracking/dataEventTrackingProvider';
import {AppPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {AppParametricSelector} from '../../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {AppState} from '../../../store/appState';
import {useDetermineShouldAddRule} from '../admin/adminOnboardingHelpers';
import {AdminOnboardingTaskDefinition, CompletedCourses} from '../admin/tasks/adminOnboardingTypes';
import {AdminSetupGuide} from './adminSetupGuide';
import {
  AdminSetupGuideConcreteScreen,
  AdminSetupGuideTaskDefinitions,
  AdminSetupGuideTasksAndProgress,
} from './adminSetupGuideTypes';

/*
 * Types.
 */

interface AdminSetupGuideTaskSelectorParams {
  taskDefinitions: AdminSetupGuideTaskDefinitions;
  shouldAddRule: boolean | undefined;
  completedCourses: CompletedCourses | undefined;
}

/*
 * Selectors.
 */

const getTasks: AppParametricSelector<AdminSetupGuideTaskSelectorParams, AdminSetupGuideTasksAndProgress> =
  memoizeOne(
    (state, params) => {
      const createTasks = (definitions: ReadonlyArray<AdminOnboardingTaskDefinition>) => {
        const tasks = definitions.map((definition, i) => ({
          key: definition.key,
          title: definition.title,
          description: definition.description,
          details: definition.details,
          isExternal: Boolean(definition.isExternal),
          number: i + 1,
          isComplete: definition.getIsTaskComplete(state, {
            completedCourses: params.completedCourses,
            shouldAddRule: params.shouldAddRule,
          }),
          startTask: definition.startTask,
        }));

        // TODO: Reconsider whether we really need this derived/denormalized state.
        const progress = {
          totalSteps: tasks.length,
          completedSteps: tasks.reduce((accum, t) => (t.isComplete ? accum + 1 : accum), 0),
          hasCompletedAllTasks: tasks.every((t) => t.isComplete),
        };

        return {
          tasks,
          progress,
        };
      };

      const demoTasks = createTasks(params.taskDefinitions.demos);
      const setupTasks = createTasks(params.taskDefinitions.setup);

      return {
        demos: demoTasks,
        setup: setupTasks,
      };
    },
    ([nextState, nextParams], [prevState, prevParams]) =>
      nextState === prevState && isEqual(nextParams, prevParams),
  );

/*
 * Props.
 */

interface AdminSetupGuideContainerProps {
  tasks: AdminSetupGuideTasksAndProgress;
  onClose: () => void;
  defaultScreen: AdminSetupGuideConcreteScreen;
  onDismissPermanentlyClick: () => void;
  panelId: AppPanelId;
}

/*
 * Component.
 */

export const AdminSetupGuideContainer: FC<AdminSetupGuideContainerProps> = (props) => {
  const {tasks, defaultScreen, onClose, onDismissPermanentlyClick, panelId} = props;

  return (
    <DataEventTrackingProvider context={{name: 'admin_setup_guide', payload: {}}}>
      <AdminSetupGuide
        defaultScreen={defaultScreen}
        tasks={tasks}
        onClose={onClose}
        onDismissPermanentlyClick={onDismissPermanentlyClick}
        panelId={panelId}
      />
    </DataEventTrackingProvider>
  );
};

export function useTasks(
  definitions: AdminSetupGuideTaskDefinitions,
  completedCourses: CompletedCourses | undefined,
): AdminSetupGuideTasksAndProgress {
  const shouldAddRule = useDetermineShouldAddRule();
  return useAppSelector((state: AppState) =>
    getTasks(state, {shouldAddRule, taskDefinitions: definitions, completedCourses}),
  );
}
